import React, { useState, useCallback, useEffect, useRef } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { useParams, useHistory } from 'react-router-dom';
import { connect } from 'react-redux';
import SiteTakeoffHeader from './header/SiteTakeoffHeader';
import Sidebar from './sidebar/Sidebar';
import MapContainer from './map/MapContainer';
import PropertySettingsDrawer from './drawer/PropertySettingsDrawer';
import useJobsite from './hooks/useJobsite';
import useAreas from './hooks/useAreas';
import useAreaRendering from './hooks/useAreaRendering';
import useAreaEditing from './hooks/useAreaEditing';
import useOverlays from './hooks/useOverlays';
import useAreaDrawing from './hooks/useAreaDrawing';
import useGeofence from './hooks/useGeofence';
import usePropertyLines from './hooks/usePropertyLines';
import useRestrictedAreas from './hooks/useRestrictedAreas';
import useJobsiteDrawing from './hooks/useJobsiteDrawing';

const useStyles = makeStyles(() => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: 'calc(100vh - 64px)',
    background: '#f5f5f5',
    position: 'relative',
    overflow: 'hidden',
  },
  body: {
    flex: 1,
    display: 'flex',
    position: 'relative',
    overflow: 'hidden',
  },
  mapArea: {
    flex: 1,
    position: 'relative',
    minWidth: 0,
    overflow: 'hidden',
  },
}));

const SiteTakeoffV2 = (props) => {
  const classes = useStyles();
  const { jobsiteId } = useParams();
  const history = useHistory();

  // Map refs — owned at the top level so hooks can use them
  const mapRef = useRef(null);
  const drawnItemsRef = useRef(null);
  const tileLayerRef = useRef(null);

  // UI state
  const [activeMode, setActiveMode] = useState('areas');
  const [settingsDrawerOpen, setSettingsDrawerOpen] = useState(false);
  const [editingAreaId, setEditingAreaId] = useState(null);

  // Layer visibility — synced from localStorage (same keys as MapLayerControl)
  const [dataLayersVisible, setDataLayersVisible] = useState(() => {
    try {
      const saved = localStorage.getItem('siteTakeoffV2_dataLayers');
      return saved ? JSON.parse(saved) : { geofence: true, propertyLines: true, restrictedAreas: true };
    } catch (_) {
      return { geofence: true, propertyLines: true, restrictedAreas: true };
    }
  });

  // Listen to localStorage changes from MapLayerControl so toggles stay in sync
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'siteTakeoffV2_dataLayers' && e.newValue) {
        try { setDataLayersVisible(JSON.parse(e.newValue)); } catch (_) {}
      }
    };
    window.addEventListener('storage', onStorage);
    // Also poll periodically since same-tab writes don't fire 'storage' event
    const interval = setInterval(() => {
      try {
        const saved = localStorage.getItem('siteTakeoffV2_dataLayers');
        if (saved) {
          const parsed = JSON.parse(saved);
          setDataLayersVisible(prev =>
            JSON.stringify(prev) !== JSON.stringify(parsed) ? parsed : prev
          );
        }
      } catch (_) {}
    }, 500);
    return () => {
      window.removeEventListener('storage', onStorage);
      clearInterval(interval);
    };
  }, []);

  // Header pickers state
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedJobsite, setSelectedJobsite] = useState(null);

  // Load full jobsite data whenever the URL :jobsiteId changes
  const { jobsite: loadedJobsite, legacyAreas, loading: jobsiteLoading } = useJobsite(props.url, jobsiteId);

  // Load takeoff / areas for the loaded jobsite
  const {
    areas,
    takeoffs,
    takeoff,
    loading: areasLoading,
    saving,
    dirty,
    addArea,
    updateArea,
    deleteArea,
    saveTakeoff,
    newTakeoff,
    switchTakeoff,
    deleteTakeoff,
    renameTakeoff,
    updateProductionRates,
    importLegacyAreas,
  } = useAreas(props.url, loadedJobsite);

  // Show legacy import when we have old jobsite.areas but no takeoffs yet
  const hasLegacyAreas =
    (legacyAreas?.length || 0) > 0 && takeoffs.length === 0 && !areasLoading;

  // Render areas on the map
  const { layerMapRef } = useAreaRendering({
    mapRef,
    drawnItemsRef,
    areas,
    onAreaClick: setEditingAreaId,
  });

  // Shape editing (vertex drag + whole-shape drag)
  const {
    editingAreaId: editingShapeAreaId,
    startEdit: startEditShape,
    stopEdit: stopEditShape,
  } = useAreaEditing({
    mapRef,
    layerMapRef,
    areas,
    updateArea,
  });

  // Area drawing tools
  const { startDraw } = useAreaDrawing({
    mapRef,
    drawnItemsRef,
    onAreaDrawn: (newAreaDraft) => {
      const created = addArea(newAreaDraft);
      setEditingAreaId(created.id);
    },
  });

  // Jobsite-layer drawing (property lines, restricted areas)
  const { startPolygonDraw, startPolygonEdit, confirmEdit, cancelEdit, undoEdit, cancelDraw, editing: editingShape } = useJobsiteDrawing(mapRef);

  // Geofence
  const geofence = useGeofence({
    url: props.url,
    jobsite: loadedJobsite,
    mapRef,
    visible: dataLayersVisible.geofence,
  });

  // Property lines
  const propertyLines = usePropertyLines({
    url: props.url,
    jobsite: loadedJobsite,
    mapRef,
    visible: dataLayersVisible.propertyLines,
  });

  // Restricted areas
  const restrictedAreas = useRestrictedAreas({
    url: props.url,
    jobsite: loadedJobsite,
    mapRef,
    visible: dataLayersVisible.restrictedAreas,
  });

  // Plan overlays (takeoff-scoped, restored from the active takeoff)
  const overlayManager = useOverlays({
    url: props.url,
    mapRef,
    jobsite: loadedJobsite,
    initialOverlays: takeoff?.overlays,
  });

  // Keep the header picker in sync with the loaded jobsite
  useEffect(() => {
    if (!loadedJobsite?._id) return;
    setSelectedJobsite({
      _id: loadedJobsite._id,
      name: loadedJobsite.name,
      address: loadedJobsite.address,
      city: loadedJobsite.city,
      state: loadedJobsite.state,
      client: loadedJobsite.client,
    });
    if (loadedJobsite.client) {
      const clientObj = typeof loadedJobsite.client === 'object'
        ? loadedJobsite.client
        : { _id: loadedJobsite.client };
      setSelectedClient(clientObj);
    }
  }, [loadedJobsite]);

  // Wrap saveTakeoff to inject live overlay geometry at save time
  const saveTakeoffWithOverlays = useCallback((name) => {
    const liveOverlays = overlayManager?.serializeForSave?.() || [];
    return saveTakeoff(name, { overlays: liveOverlays });
  }, [saveTakeoff, overlayManager]);

  const handleJobsiteChange = useCallback((jobsite) => {
    setSelectedJobsite(jobsite);
    setEditingAreaId(null);
    if (jobsite?.client) {
      const clientObj = typeof jobsite.client === 'object' ? jobsite.client : { _id: jobsite.client };
      setSelectedClient(clientObj);
    }
    if (jobsite?._id) {
      history.push(`/site-takeoff-v2/${jobsite._id}`);
    }
  }, [history]);

  const handleClientChange = useCallback((client) => {
    setSelectedClient(client);
    setSelectedJobsite(null);
    setEditingAreaId(null);
    if (!client) {
      history.push('/site-takeoff-v2');
    }
  }, [history]);

  // Setup status — derived from live state
  const setupStatus = {
    areasMapped: areas.length > 0,
    geofenceSet: !!loadedJobsite?.radius,
    propertyLinesVerified:
      (propertyLines.countyLines.length + propertyLines.manualLines.length) > 0,
  };
  const isSetupComplete = Object.values(setupStatus).every(Boolean);

  return (
    <div className={classes.root}>
      <SiteTakeoffHeader
        url={props.url}
        clients={props.clients}
        selectedClient={selectedClient}
        selectedJobsite={selectedJobsite}
        onClientChange={handleClientChange}
        onJobsiteChange={handleJobsiteChange}
        setupComplete={isSetupComplete}
        onSettingsClick={() => setSettingsDrawerOpen(true)}
      />

      <div className={classes.body}>
        <div className={classes.mapArea}>
          <MapContainer
            jobsite={loadedJobsite}
            loading={jobsiteLoading || areasLoading}
            mapRef={mapRef}
            drawnItemsRef={drawnItemsRef}
            tileLayerRef={tileLayerRef}
            editingShape={editingShape}
            onConfirmEdit={confirmEdit}
            onCancelEdit={cancelEdit}
            onUndoEdit={undoEdit}
          />
        </div>

        <Sidebar
          url={props.url}
          activeMode={activeMode}
          onModeChange={setActiveMode}
          editingAreaId={editingAreaId}
          onEditArea={setEditingAreaId}
          onEditAreaClose={() => { stopEditShape(); setEditingAreaId(null); }}
          editingShapeAreaId={editingShapeAreaId}
          onStartEditShape={startEditShape}
          onStopEditShape={stopEditShape}
          jobsite={loadedJobsite}
          areas={areas}
          takeoffs={takeoffs}
          takeoff={takeoff}
          saving={saving}
          dirty={dirty}
          onDrawArea={(tool) => startDraw(tool)}
          legacyAreas={legacyAreas}
          hasLegacyAreas={hasLegacyAreas}
          onImportLegacyAreas={async () => {
            const name = window.prompt('Name for imported takeoff:', 'Imported Areas');
            if (!name) return;
            await importLegacyAreas(legacyAreas, name);
            setEditingAreaId(null);
          }}
          onUpdateArea={updateArea}
          onDeleteArea={deleteArea}
          onSaveTakeoff={saveTakeoffWithOverlays}
          onNewTakeoff={() => { newTakeoff(); setEditingAreaId(null); }}
          onSwitchTakeoff={(id) => { switchTakeoff(id); setEditingAreaId(null); }}
          onDeleteTakeoff={(id) => { deleteTakeoff(id); setEditingAreaId(null); }}
          onRenameTakeoff={renameTakeoff}
          overlayManager={overlayManager}
        />

        <PropertySettingsDrawer
          open={settingsDrawerOpen}
          onClose={() => setSettingsDrawerOpen(false)}
          url={props.url}
          jobsite={loadedJobsite}
          setupStatus={setupStatus}
          geofence={geofence}
          propertyLines={propertyLines}
          restrictedAreas={restrictedAreas}
          startPolygonDraw={startPolygonDraw}
          startPolygonEdit={startPolygonEdit}
          areas={areas}
          rateOverrides={takeoff?.productionRates || {}}
          onUpdateRates={updateProductionRates}
        />
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  url: state.url,
  user: state.user,
  clients: state.clients,
});

export default connect(mapStateToProps)(SiteTakeoffV2);
